import { useFormContext } from 'react-hook-form';
import type { TLocalize } from '@hooks/use-localize';
import type { IHistoryDemographicsFormValues, IHistoryDemographicsModel, IHistoryOption } from '@/types/history/history-demographics';
import FieldError from '@components/field-error';

interface IEmploymentSectionProps {
    model: IHistoryDemographicsModel;
    t: TLocalize;
}

/** Employment, lost wage reimbursement, and income. */
const EmploymentSection = ({ model, t }: IEmploymentSectionProps) => {
    const {
        register,
        watch,
        formState: { errors },
    } = useFormContext<IHistoryDemographicsFormValues>();

    const radioGroup = (field: keyof IHistoryDemographicsFormValues, options: IHistoryOption[]) => (
        <div className="col-xs-12">
            {options.map((option) => (
                <div className="radio" key={option.value}>
                    <label>
                        <input type="radio" value={option.value} {...register(field)} /> {option.label}
                    </label>
                </div>
            ))}
        </div>
    );

    const isEmployed = watch('isCurrentlyEmployed') === '1';
    const notReimbursed = model.wageReimbursementPtoValues.map(String).includes(watch('medicalPto'));
    const showWantsReimbursement = isEmployed && model.isLwrAvailable && notReimbursed;

    return (
        <>
            <div className='section-heading'>Employment</div>

            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t('DashLabel_AreYouCurrentlyEmployed', 'Are you currently employed')}?
                    <FieldError message={errors.isCurrentlyEmployed?.message} />
                </label>
                <div className="col-xs-12">
                    <label className="radio-inline">
                        <input type="radio" value="1" {...register('isCurrentlyEmployed')} />{' '}
                        {t('Word_Yes', 'Yes')}
                    </label>
                    <label className="radio-inline">
                        <input type="radio" value="0" {...register('isCurrentlyEmployed')} />{' '}
                        {t('Word_No', 'No')}
                    </label>
                </div>
            </div>

            {isEmployed && (
                <div className="sub-question">
                    <div className="form-group">
                        <label className="control-label col-xs-12">
                            {t('Word_Occupation', 'Occupation')}:
                            <FieldError message={errors.occupation?.message} />
                        </label>
                        <div className="col-xs-12 col-md-8">
                            <input className="form-control" maxLength={32} {...register('occupation')} />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="control-label col-xs-12">
                            {t('DashLabel_DoesYourOccupationRequireHeavyLifting', 'Does your occupation require heavy lifting')}?
                            <FieldError message={errors.occupationHasHeavyLifting?.message} />
                        </label>
                        <div className="col-xs-12">
                            <label className="radio-inline">
                                <input type="radio" value="1" {...register('occupationHasHeavyLifting')} />{' '}
                                {t('Word_Yes', 'Yes')}
                            </label>
                            <label className="radio-inline">
                                <input type="radio" value="0" {...register('occupationHasHeavyLifting')} />{' '}
                                {t('Word_No', 'No')}
                            </label>
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="control-label col-xs-12">
                            {t('DashLabel_WillYouBeReimbursedForLostWages', 'Will you be reimbursed for lost wages')}?
                            <FieldError message={errors.medicalPto?.message} />
                        </label>
                        {radioGroup('medicalPto', model.medicalPtoOptions)}
                    </div>

                    {model.isLwrAvailable && (
                        <>
                            <div className="form-group">
                                <label className="control-label col-xs-12">
                                    {t(
                                        'DashLabel_ItWillBeNecessaryToTakeBetween2And4WeeksOffOfWorkT',
                                        'It will be necessary to take between 2 and 4 weeks off of work to donate. Additionally, you will not be able to fly for one week. If you decide to donate, how important is it to you that you receive lost wage reimbursement',
                                    )}
                                    ?<FieldError message={errors.wageReimbursementImportance?.message} />
                                </label>
                                {radioGroup('wageReimbursementImportance', model.wageImportanceOptions)}
                            </div>

                            <div className="form-group">
                                <label className="control-label col-xs-12">
                                    {t('DashLabel_WhatIsYourAnnualIncome', 'What is your annual income')}?
                                    <FieldError message={errors.annualIncomeRange?.message} />
                                </label>
                                {radioGroup('annualIncomeRange', model.annualIncomeOptions)}
                            </div>

                            {showWantsReimbursement && (
                                <div className="form-group">
                                    <label className="control-label col-xs-12">
                                        {t(
                                            'DashLabel_DoYouWantToApplyForReimbursementOfLostWages',
                                            'Do you want to apply for reimbursement of lost wages',
                                        )}
                                        ?<FieldError message={errors.wantsWageReimbursement?.message} />
                                    </label>
                                    <div className="col-xs-12">
                                        <label className="radio-inline">
                                            <input type="radio" value="1" {...register('wantsWageReimbursement')} />{' '}
                                            {t('Word_Yes', 'Yes')}
                                        </label>
                                        <label className="radio-inline">
                                            <input type="radio" value="0" {...register('wantsWageReimbursement')} />{' '}
                                            {t('Word_No', 'No')}
                                        </label>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </>
    );
};

export default EmploymentSection;
